import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { ChevronDown, Users, Check } from 'lucide-react'

interface TeamSwitcherProps {
  currentTeamId?: string
  currentTeamName?: string
}

export const TeamSwitcher: React.FC<TeamSwitcherProps> = ({ 
  currentTeamId, 
  currentTeamName 
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const { userTeams } = useAuth()
  const navigate = useNavigate()
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleTeamSelect = (teamId: string) => {
    setIsOpen(false)
    navigate(`/dashboard/team/${teamId}`)
  }

  const teams = userTeams || []
  
  // Don't render if user has no teams or only one team
  if (teams.length <= 1) {
    return (
      <div className="flex items-center space-x-2 px-4 py-2 rounded-full bg-white/40 border border-white/30 text-slate-600">
        <Users className="h-4 w-4" />
        <span className="font-medium text-sm">{currentTeamName || 'Team'}</span>
      </div>
    )
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 rounded-full bg-white/40 border border-white/30 text-slate-600 hover:bg-white/60 hover:text-slate-700 transition-all duration-300 hover:shadow-sm"
      >
        <Users className="h-4 w-4" />
        <span className="font-medium text-sm">
          {currentTeamName || 'Select Team'}
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
            Your Teams
          </div>
          
          {teams.map((membership) => {
            const team = membership.teams
            const isCurrentTeam = team?.id === currentTeamId
            
            return (
              <button
                key={membership.id}
                onClick={() => team?.id && handleTeamSelect(team.id)}
                className={`w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors flex items-center justify-between ${
                  isCurrentTeam ? 'bg-indigo-50 text-indigo-700' : 'text-gray-700'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium ${
                    isCurrentTeam 
                      ? 'bg-indigo-100 text-indigo-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {team?.name?.charAt(0).toUpperCase() || 'T'}
                  </div>
                  <div>
                    <div className="font-medium">{team?.name || 'Unknown Team'}</div>
                    <div className="text-xs text-gray-500 capitalize">{membership.role}</div>
                  </div>
                </div>
                
                {isCurrentTeam && (
                  <Check className="h-4 w-4 text-indigo-600" />
                )}
              </button>
            )
          })}
          
          <div className="border-t border-gray-100 mt-2 pt-2">
            <button
              onClick={() => {
                setIsOpen(false)
                navigate('/dashboard')
              }}
              className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
            >
              ← Back to All Teams
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default TeamSwitcher 